import { Box, Slider, Typography } from "@mui/material";

interface LayerStyleEditorProps {
  color: string;
  opacity: number;
  onColorChange: (color: string) => void;
  onOpacityChange: (opacity: number) => void;
  onOpacityCommit?: (opacity: number) => void;
  size?: "small" | "medium";
  showLabels?: boolean;
  variant?: "light" | "dark";
}

/**
 * Compact colour + opacity picker. The colour input is a native HTML5 swatch;
 * opacity is a slider 0..1. onOpacityCommit fires when the slider is released,
 * for callers that want to PATCH only on commit instead of every drag step.
 */
const LayerStyleEditor: React.FC<LayerStyleEditorProps> = ({
  color,
  opacity,
  onColorChange,
  onOpacityChange,
  onOpacityCommit,
  size = "medium",
  showLabels = true,
  variant = "light",
}) => {
  const swatch = size === "small" ? 22 : 32;
  const isDark = variant === "dark";

  const labelColor = isDark ? "rgba(226, 232, 240, 0.85)" : "text.secondary";
  const swatchBorder = isDark
    ? "1px solid rgba(148, 163, 184, 0.6)"
    : "1px solid rgba(148, 163, 184, 0.4)";

  const sliderSx = isDark
    ? {
        flex: 1,
        minWidth: 80,
        color: "#00d4ad",
        "& .MuiSlider-rail": { opacity: 1, backgroundColor: "rgba(148, 163, 184, 0.45)" },
        "& .MuiSlider-track": { border: "none" },
        "& .MuiSlider-thumb": {
          backgroundColor: "#00d4ad",
          "&:hover, &.Mui-focusVisible": { boxShadow: "0 0 0 6px rgba(0, 212, 173, 0.22)" },
          "&.Mui-active": { boxShadow: "0 0 0 10px rgba(0, 212, 173, 0.28)" },
        },
      }
    : { flex: 1, minWidth: 80 };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: "100%" }}>
      {showLabels && (
        <Typography variant="caption" sx={{ color: labelColor, minWidth: 40 }}>
          Colour
        </Typography>
      )}
      <Box
        component="input"
        type="color"
        value={color}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onColorChange(e.target.value)}
        sx={{
          width: swatch,
          height: swatch,
          padding: 0,
          border: swatchBorder,
          borderRadius: "4px",
          background: "transparent",
          cursor: "pointer",
          "&::-webkit-color-swatch-wrapper": { padding: 0 },
          "&::-webkit-color-swatch": { border: "none", borderRadius: "3px" },
        }}
        aria-label="Layer fill colour"
      />
      {showLabels && (
        <Typography variant="caption" sx={{ color: labelColor, minWidth: 50 }}>
          Opacity
        </Typography>
      )}
      <Slider
        size={size}
        value={opacity}
        min={0}
        max={1}
        step={0.05}
        onChange={(_, value) => onOpacityChange(Array.isArray(value) ? value[0] : value)}
        onChangeCommitted={(_, value) =>
          onOpacityCommit?.(Array.isArray(value) ? value[0] : value)
        }
        valueLabelDisplay="auto"
        valueLabelFormat={(v) => `${Math.round(v * 100)}%`}
        sx={sliderSx}
        aria-label="Layer opacity"
      />
    </Box>
  );
};

export default LayerStyleEditor;
